import { SO_DEV_STORY_URL_REGEX } from '@asgard/utils';
import { faCloudDownload } from '@fortawesome/free-solid-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/shared/Button';
import FormGroup from 'components/shared/FormGroup';
import InputText from 'components/shared/InputText';
import { LinkText } from 'components/shared/LinkText';
import Notification from 'components/shared/Notification';
import { Heading1 } from 'components/shared/Typography';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { Trans } from 'next-i18next';
import { useState } from 'react';
import StackOverflowService from 'services/stack-overflow.service';
import { convertObjectToBlob } from 'utils/blog';
import { getErrorMessage } from 'utils/errors';
import { downloadBlob } from 'utils/file-download';
import { getPublicPath } from 'utils/paths';
import {
  StyledContainer,
  StyledContent,
  StyledForm,
  StyledHero,
  StyledImage,
  StyledLead,
  StyledMobileImage
} from './StackOverflowHero.styled';

const StackOverflowHero: React.FC = (): JSX.Element => {
  const { t } = useI18n('stackOverflowLanding');

  const { addErrorToast } = useToasts();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const val = e.target.value;
    const result = SO_DEV_STORY_URL_REGEX.test(val);

    if (result) {
      setError('');
    } else {
      setError(t('messages.error.invalidStackOverflowUrl') || '');
    }

    setUrl(val);
  };

  const handleImportClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.preventDefault();

    if (!url || isSubmitting || !!error) {
      return;
    }

    try {
      setIsSubmitting(true);

      const jsonMac = await StackOverflowService.downloadDevStoryAsMAC(url);
      const jsonBlob = convertObjectToBlob(jsonMac);
      const fileName = 'my-mac-from-dev-stories.json';

      downloadBlob(fileName, jsonBlob);

      setIsSubmitting(false);
      setUrl('');
      setError('');
    } catch (ex) {
      console.error(ex);

      setIsSubmitting(false);

      addErrorToast(
        t('messages.error.importStackOverflow', {
          error: getErrorMessage(ex)
        })
      );
    }
  };

  return (
    <StyledHero>
      <StyledContainer>
        <StyledContent>
          <Heading1>
            <div
              dangerouslySetInnerHTML={{
                __html: t('hero.title')
              }}
            ></div>
          </Heading1>
          <StyledLead>
            <p>
              <Trans
                ns={'stackOverflowLanding'}
                i18nKey={'hero.subTitle'}
                components={{
                  online: <LinkText href="/" />
                }}
              ></Trans>
            </p>
          </StyledLead>
          <StyledMobileImage>
            <img
              src={getPublicPath('/images/sOLanding/bin-mobile.png')}
              alt=""
            />
          </StyledMobileImage>

          <div>
            <Trans
              ns={'stackOverflowLanding'}
              i18nKey={'hero.description'}
              components={{
                anounced: <strong />,
                stackOverflow: (
                  <a
                    href="https://meta.stackoverflow.com/questions/415293/sunsetting-jobs-developer-story"
                    target={'-blank'}
                  ></a>
                ),
                paste: <strong />
              }}
            ></Trans>
          </div>

          <StyledForm>
            <FormGroup
              forId="devStoryUrl"
              label={t('hero.label')}
              error={error}
            >
              <InputText
                id="devStoryUrl"
                placeholder={t('hero.placeholder') || ''}
                variant={error ? 'error' : ''}
                onChange={handleUrlChange}
                value={url}
                readOnly
              />
            </FormGroup>
            <Button
              type="submit"
              variant="brandTalent"
              disabled
              onClick={handleImportClick}
            >
              {!isSubmitting && <FontAwesomeIcon icon={faCloudDownload} />}
              {isSubmitting && <FontAwesomeIcon icon={faSpinnerThird} spin />}

              <span>{t('hero.CTA')}</span>
            </Button>
          </StyledForm>

          <Notification variant="stackOverflow">
            {t('hero.notification')}
          </Notification>
        </StyledContent>
        <StyledImage>
          <img src={getPublicPath('/images/sOLanding/bin.png')} alt="" />
        </StyledImage>
      </StyledContainer>
    </StyledHero>
  );
};

export default StackOverflowHero;
